import { ColumnProps } from "antd/lib/table";
import { RequestHistory, RequestHistoryFilter } from "models/RequestHistory";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutCell, LayoutHeader, OneLineText } from "react3l-ui-library";
import nameof from "ts-nameof.macro";
import { filterService } from "services/page-services/filter-service";
import { Moment } from "moment";
import { formatDateTime } from "helpers/date-time";
import { DOCUMENT_PERMISSION_HISTORY_DETAIL_ROUTE } from "config/route-consts";
import { OrderType } from "react3l-common";
import { requestHistoryService } from "services/page-services/request-history-service";
import { useHistory } from "react-router-dom";
import { permissionRepository } from "repositories/permission-repository";
import { queryStringService } from "services/page-services/query-string-service";
import { View24 } from "@carbon/icons-react";
import { formatNumber } from "helpers/number";

/* Expand a history content into one row per documentPermissionFieldMappings
   element. All other fields are kept identical across rows; only the
   permission name differs. Returns a single row when there are no mappings. */
function expandContentByPermission(content: any): any[] {
    const mappings: any[] = content?.documentPermissionFieldMappings ?? [];
    if (!mappings.length) {
        return [{ ...content, __rowKey: `${content?.id ?? 0}-0`, __permissionName: "" }];
    }
    return mappings.map((mapping: any, index: number) => {
        const actions = Array.isArray(mapping?.documentAction)
            ? mapping.documentAction
            : mapping?.documentAction
            ? [mapping.documentAction]
            : [];
        const permissionName = actions
            .map((action: any) => action?.name)
            .filter(Boolean)
            .join(", ");
        return {
            ...content,
            __rowKey: `${content?.id ?? 0}-${index}`,
            __permissionName: permissionName,
        };
    });
}

export function usePermissionHistoryListHook() {
    const [translate] = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [dataHistoryPreview, setDataHistoryPreview] = useState([]);
    const [isSpinning, setIsSpinning] = useState(false);
    const { modelFilter: filter, dispatchFilter } = filterService.useModelFilter(
        RequestHistoryFilter,
        {
            orderBy: "savedAt",
            orderType: OrderType.DESC,
        }
    );

    const {
        requestHistoryList,
        requestHistoryCount,
        rowSelection,
        handleCompareHistory,
        handlePagination,
    } = requestHistoryService.useHistoryRequestList<
        RequestHistory,
        RequestHistoryFilter
    >(
        permissionRepository.listRequestHistory,
        permissionRepository.countRequestHistory,
        filter,
        dispatchFilter,
        DOCUMENT_PERMISSION_HISTORY_DETAIL_ROUTE,
        "versionId"
    );

    const { requestId } = queryStringService.useGetQueryString("requestId");
    const [typeCode, setTypeCode] = useState<string>("");

    React.useEffect(() => {
        if (requestId) {
            const sub = permissionRepository
                .get(Number(requestId))
                .subscribe({
                    next: (permission) => setTypeCode(permission?.typeCode ?? ""),
                    error: () => {},
                });
            return () => sub.unsubscribe();
        }
    }, [requestId]);

    const history = useHistory();
    const handleGoBack = React.useCallback(() => {
        history.goBack();
    }, [history]);

    const previewHistory = React.useCallback((requestHistory: any) => {
        setIsSpinning(true);
        permissionRepository.getRequestHistory(requestHistory?.versionId)
            .subscribe({
                next: (res: RequestHistory) => {
                    setIsVisible(true);
                    setDataHistoryPreview(
                        res?.content ? expandContentByPermission(res.content) : []
                    );
                    setIsSpinning(false);
                },
                error: () => {
                    setIsSpinning(false);
                },
            });
    }, [])

    const handleCloseModal = React.useCallback(() => {
        setIsVisible(false);
    }, [])

    const columns: ColumnProps<any>[] = React.useMemo(
        () => [
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("appUsers.histories.appUser")}
                    />
                ),
                key: nameof("actor"),
                dataIndex: nameof("actor"),
                ellipsis: true,
                render(actor: string) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={actor} />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("appUsers.histories.action")}
                    />
                ),
                key: nameof("actionName"),
                dataIndex: nameof("actionName"),
                ellipsis: true,
                render(actionName: string) {
                    const entity =
                        typeCode === "Document"
                            ? "Folder"
                            : typeCode === "File"
                            ? "File"
                            : "";
                    const action =
                        actionName === "Create"
                            ? "create"
                            : actionName === "Update"
                            ? "update"
                            : "";
                    const label =
                        action && entity
                            ? translate(`permissions.history.${action}${entity}`)
                            : actionName;
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={label} />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("appUsers.histories.savedAt")}
                    />
                ),
                key: nameof("savedAt"),
                dataIndex: nameof("savedAt"),
                ellipsis: true,
                render(...params: [Moment, RequestHistory, number]) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={formatDateTime(params[0])} />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader orderType="left" title={translate("general.action")} />
                ),
                key: "action",
                dataIndex: "action",
                fixed: "right",
                width: 110,
                render(id: number, requestHistory: RequestHistory, index: number) {
                    return (
                        <div className="d-flex button-action-table missions-dxg">
                            <div className="d-flex">
                                <div className="enable-dxg">
                                    <View24 className="btn-missions-green" onClick={() => previewHistory(requestHistory)} />
                                </div>
                            </div>
                        </div>
                    );
                },
            },
        ],
        [translate, previewHistory, typeCode]
    );

    const columnsModalHistoryPreview: ColumnProps<any>[] = React.useMemo(
        () => [
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("general.columns.index")}
                    />
                ),
                width: 50,
                ellipsis: true,
                key: "stt",
                dataIndex: "stt",
                render(...params: [number, any, number]) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText
                                value={formatNumber(params[2] + 1)}
                                countCharacters={50}
                            />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("permissions.code")}
                    />
                ),
                key: "code",
                dataIndex: "code",
                ellipsis: true,
                render(code: string) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={code} countCharacters={100} />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("permissions.name")}
                    />
                ),
                key: "name",
                dataIndex: "name",
                ellipsis: true,
                render(name: string) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={name} countCharacters={100} />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("permissions.classification")}
                    />
                ),
                key: "typeCode",
                dataIndex: "typeCode",
                ellipsis: true,
                render(typeCode: string) {
                    const label =
                        typeCode === "Document"
                            ? translate("permissions.folder")
                            : typeCode === "File"
                            ? translate("permissions.file")
                            : "";
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={label} countCharacters={100} />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("permissions.permission")}
                    />
                ),
                key: "permission",
                dataIndex: "__permissionName",
                ellipsis: true,
                render(permissionName: string) {
                    /* one permission per row (see expandContentByPermission) */
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={permissionName} countCharacters={100} />
                        </LayoutCell>
                    );
                },
            },
        ],
        [translate]
    );

    return {
        translate,
        handleCompareHistory,
        handleGoBack,
        handlePagination,
        requestHistoryList,
        requestHistoryCount,
        rowSelection,
        columns,
        filter,
        columnsModalHistoryPreview,
        isVisible,
        dataHistoryPreview,
        handleCloseModal,
        isSpinning,
    };
}
